import { useEffect, useRef } from 'react';
import { useTheme } from 'context/ThemeContext.jsx';
import { useAuth } from 'context/AuthContext.jsx';
import V1V2Shell from 'components/layout/V1V2Shell.jsx';
import V3Shell from 'components/layout/V3Shell.jsx';
import AmbientLayers from 'components/layout/AmbientLayers.jsx';
import BootSequence from 'components/BootSequence.jsx';
import ScrollToTop from 'components/ScrollToTop.jsx';
import OnboardingModal from 'components/OnboardingModal.jsx';
import { markSessionBooted, readSessionBooted, shouldBoot } from 'lib/bootSequence.mjs';

/**
 * Picks the chrome for the current theme. The routes underneath do not change.
 *
 * This is the ONLY place a shell is chosen. Everything else in the app is
 * theme-agnostic and lets the [data-theme] cascade do the work — which is the
 * whole point of the exercise, given public/index.html has 570 data-theme
 * references scattered through its render functions.
 *
 * It is also where the boot sequence fires. This component mounts when the user
 * reaches a signed-in route, which is exactly when the original called
 * triggerBoot() from showDashboard().
 */
export default function ThemeShell() {
  const {
    isV3, theme, bootEnabled, bootRunning, startBoot, endBoot,
    onboardingSeen, onboardingOpen, openOnboarding, dismissOnboarding,
  } = useTheme();
  const { name, isAdminPreview } = useAuth();
  const tried = useRef(false);
  // Read once. Marking the session on the way out must not flip this mid-run.
  const repeatBoot = useRef(readSessionBooted());

  useEffect(() => {
    // Once per entry into the signed-in app — not on every theme change, or
    // switching V1→V2 would replay the whole sequence mid-session.
    if (tried.current) return;
    tried.current = true;

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (!isAdminPreview && shouldBoot({ theme, bootEnabled, reducedMotion })) startBoot();
  }, [theme, bootEnabled, startBoot, isAdminPreview]);

  // The first-run guide waits for the boot sequence to clear rather than firing
  // on a fixed 4.2s timer as the original did — with BOOT off the old timer
  // still waited out a sequence that was never going to play.
  useEffect(() => {
    if (isAdminPreview || onboardingSeen || bootRunning) return;
    openOnboarding();
  }, [onboardingSeen, bootRunning, openOnboarding, isAdminPreview]);

  return (
    <>
      <ScrollToTop />
      <AmbientLayers />
      {isV3 ? <V3Shell /> : <V1V2Shell />}
      {bootRunning && (
        <BootSequence
          name={name}
          fast={repeatBoot.current}
          onDone={() => {
            markSessionBooted();
            endBoot();
          }}
        />
      )}
      <OnboardingModal open={onboardingOpen && !bootRunning} onClose={dismissOnboarding} />
    </>
  );
}
